import { useEffect } from "react";
import {
	DEVICE_SHARED_VOLUME_POLL_INTERVAL_MS,
	DEVICE_SHARED_VOLUME_RETRY_INTERVAL_MS,
	isDeviceSharedVolumeListingRefreshing,
	isDeviceSharedVolumeTransferring,
} from "@/lib/device-shared-volumes";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import {
	selectEffectiveEnvironmentId,
	selectEffectiveEnvironmentRunnerName,
	useChatStore,
} from "@/stores/useChatStore";
import { hasVolumes, useDeviceSharedVolumeStore } from "@/stores/useDeviceSharedVolumeStore";

/**
 * 開いているチャット (下書きなら送信先) の環境を追い、デバイスのエージェントなら付いている共有ボリュームを取る (取れなければ
 * 間をおいて取り直す)。ファイルのツリーに共有ボリュームを出すか (付いているボリュームがあるか) を返し、それが変わるときだけ
 * 再描画させる。追い直すのは環境が変わったときだけで、下書きから同じ環境のチャットになっても追い直さない。
 */
export function useDeviceSharedVolumes(): boolean {
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const onDevice = useChatStore((s) => selectEffectiveEnvironmentRunnerName(s) !== null);
	const visible = useDeviceSharedVolumeStore(
		(s) =>
			onDevice &&
			environmentId !== null &&
			s.environmentId === environmentId &&
			hasVolumes(s.overview),
	);

	useEffect(() => {
		useDeviceSharedVolumeStore.getState().track(environmentId);
	}, [environmentId]);

	useEffect(() => {
		if (environmentId === null || !onDevice) return;
		let stopped = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const load = async () => {
			const store = useDeviceSharedVolumeStore.getState();
			if (store.overview !== null) return;
			try {
				await store.refresh({ scan: false, relist: "none" });
			} catch (err) {
				if (!stopped) {
					timer = setTimeout(
						() => reportSharedVolumeFailure(load(), "共有ボリュームの状態を取得できませんでした。"),
						DEVICE_SHARED_VOLUME_RETRY_INTERVAL_MS,
					);
				}
				throw err;
			}
		};
		reportSharedVolumeFailure(load(), "共有ボリュームの状態を取得できませんでした。");
		return () => {
			stopped = true;
			clearTimeout(timer);
		};
	}, [environmentId, onDevice]);

	return visible;
}

/**
 * 取り込み・反映 (頼んでからランナーが報告し始めるまでを含む) か、クラウドの一覧の取り直しが進んでいるか。
 */
export function useDeviceSharedVolumeActivity(): boolean {
	return useDeviceSharedVolumeStore(
		(s) =>
			s.activityPollsLeft > 0 ||
			(s.overview !== null &&
				(isDeviceSharedVolumeTransferring(s.overview) ||
					isDeviceSharedVolumeListingRefreshing(s.overview.volumes))),
	);
}

/**
 * 共有ボリュームを出したら手元の変更を数え直し、出している間、転送中とクラウドの一覧の取り直し中は状態を取り直し
 * 続ける。取り直しは前の結果 (失敗も含む) を受けてから次を予約するので、重ならず、失敗しても止まらない。
 */
export function useDeviceSharedVolumeSync(): void {
	const polling = useDeviceSharedVolumeActivity();

	useEffect(() => {
		reportSharedVolumeFailure(
			useDeviceSharedVolumeStore.getState().refresh({ scan: true, relist: "missing" }),
			"共有ボリュームの中身を取得できませんでした。",
		);
	}, []);

	useEffect(() => {
		if (!polling) return;
		let stopped = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const tick = async () => {
			try {
				await useDeviceSharedVolumeStore.getState().poll();
			} finally {
				if (!stopped) schedule();
			}
		};
		const schedule = () => {
			timer = setTimeout(
				() => reportSharedVolumeFailure(tick(), "共有ボリュームの状態を取得できませんでした。"),
				DEVICE_SHARED_VOLUME_POLL_INTERVAL_MS,
			);
		};
		schedule();
		return () => {
			stopped = true;
			clearTimeout(timer);
		};
	}, [polling]);
}
